// PPTX renderer — walks the resolved sections array and builds a 16:9 deck.
// Pure renderer: no fetching, no toasts; throws on failure (modal owns UX).
import { formatValue, hexToPptx } from './exportUtils';

const GREEN = '10B981';
const CYAN = '06B6D4';
const DARK = '1A202C';
const SECONDARY = '64748B';
const SUBTLE = 'F8FAFC';
const BORDER = 'E2E8F0';
const WHITE = 'FFFFFF';
const FONT = 'Arial';

const MASTER_NAME = 'CONTENT';
const SLIDE_W = 10;
const CONTENT_X = 0.4;
const CONTENT_W = 9.2;
const ROWS_PER_SLIDE = 12;
const MAX_SLIDES_PER_SECTION = 4;
const MAX_CHART_BUCKETS = 31;

const slug = (str) => String(str)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const alignOf = (col) => col.align
  || (col.type === 'number' || col.type === 'percent' || col.type === 'duration' ? 'right' : 'left');

// Fixed widths for typed numeric columns; text columns share the remainder
const columnWidths = (columns) => {
  const fixed = { number: 1.3, percent: 1.1, duration: 1.2, date: 1.5 };
  const widths = columns.map((c) => fixed[c.type] || 0);
  const used = widths.reduce((a, b) => a + b, 0);
  const textCount = widths.filter((w) => w === 0).length;
  if (textCount === 0) {
    const scale = CONTENT_W / used;
    return widths.map((w) => w * scale);
  }
  const textW = Math.max((CONTENT_W - used) / textCount, 1);
  return widths.map((w) => (w === 0 ? textW : w));
};

// Fit logo inside a max box preserving aspect ratio
const fitLogo = (logo, maxW, maxH) => {
  const ratio = logo.width > 0 && logo.height > 0 ? logo.width / logo.height : 1;
  let w = maxW;
  let h = maxW / ratio;
  if (h > maxH) {
    h = maxH;
    w = maxH * ratio;
  }
  return { w, h };
};

const addSectionHeader = (slide, pptx, title, primary, accent) => {
  slide.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: '100%', h: 0.85, fill: { color: primary } });
  slide.addShape(pptx.ShapeType.rect, { x: 0, y: 0.85, w: 2, h: 0.07, fill: { color: accent } });
  slide.addText(title, {
    x: CONTENT_X, y: 0.18, w: CONTENT_W, h: 0.5,
    fontSize: 22, bold: true, color: WHITE, fontFace: FONT,
  });
};

const addTitleSlide = (pptx, meta, primary, accent) => {
  const slide = pptx.addSlide();
  slide.background = { color: primary };

  // Subtle accent geometry
  slide.addShape(pptx.ShapeType.ellipse, { x: 7.4, y: -1.4, w: 4.2, h: 4.2, fill: { color: accent, transparency: 78 } });
  slide.addShape(pptx.ShapeType.ellipse, { x: 8.5, y: 3.7, w: 3, h: 3, fill: { color: accent, transparency: 86 } });
  slide.addShape(pptx.ShapeType.rect, { x: 0, y: 3.55, w: '100%', h: 0.06, fill: { color: accent } });

  if (meta.logo) {
    const { w, h } = fitLogo(meta.logo, 2.2, 0.9);
    slide.addShape(pptx.ShapeType.roundRect, {
      x: 0.6, y: 0.5, w: w + 0.3, h: h + 0.2,
      fill: { color: WHITE }, rectRadius: 0.08,
    });
    slide.addImage({ data: meta.logo.dataUrl, x: 0.75, y: 0.6, w, h });
  } else {
    slide.addText(meta.companyName.toUpperCase(), {
      x: 0.6, y: 0.5, w: 8, h: 0.5,
      fontSize: 20, bold: true, color: WHITE, charSpacing: 2, fontFace: FONT,
    });
  }

  slide.addText(meta.title, {
    x: 0.6, y: 1.95, w: 8.8, h: 0.75,
    fontSize: 40, bold: true, color: WHITE, fontFace: FONT,
  });
  slide.addText(meta.subtitle, {
    x: 0.6, y: 2.75, w: 8.8, h: 0.5,
    fontSize: 18, color: WHITE, fontFace: FONT,
  });

  // Period card
  slide.addShape(pptx.ShapeType.roundRect, {
    x: 0.6, y: 3.95, w: 4.2, h: 1.15,
    fill: { color: WHITE }, rectRadius: 0.08,
  });
  slide.addText('REPORT PERIOD', {
    x: 0.85, y: 4.08, w: 3.7, h: 0.25,
    fontSize: 9, bold: true, color: SECONDARY, charSpacing: 1, fontFace: FONT,
  });
  slide.addText(meta.dateRangeLabel, {
    x: 0.85, y: 4.33, w: 3.7, h: 0.4,
    fontSize: 15, bold: true, color: DARK, fontFace: FONT,
  });
  slide.addText(`Generated ${meta.generatedAt}`, {
    x: 0.85, y: 4.73, w: 3.7, h: 0.25,
    fontSize: 8, color: SECONDARY, fontFace: FONT,
  });
};

const addSummarySlide = (pptx, section, primary, accent) => {
  const slide = pptx.addSlide({ masterName: MASTER_NAME });
  addSectionHeader(slide, pptx, section.label, primary, accent);

  const cardColors = [primary, accent, GREEN, CYAN];
  const cardW = 2.2;
  const cardH = 1.75;

  section.metrics.slice(0, 8).forEach((metric, i) => {
    const col = i % 4;
    const row = Math.floor(i / 4);
    const x = CONTENT_X + col * 2.35;
    const y = 1.25 + row * 1.95;
    const color = cardColors[i % cardColors.length];

    slide.addShape(pptx.ShapeType.roundRect, {
      x, y, w: cardW, h: cardH,
      fill: { color: WHITE }, line: { color: BORDER, width: 1 }, rectRadius: 0.05,
    });
    slide.addShape(pptx.ShapeType.rect, {
      x: x + 0.05, y, w: cardW - 0.1, h: 0.06, fill: { color },
    });
    slide.addText(String(metric.label).toUpperCase(), {
      x: x + 0.15, y: y + 0.25, w: cardW - 0.3, h: 0.45,
      fontSize: 9, bold: true, color: SECONDARY, charSpacing: 1, fontFace: FONT,
    });
    slide.addText(formatValue(metric.value, metric.type), {
      x: x + 0.15, y: y + 0.8, w: cardW - 0.3, h: 0.6,
      fontSize: 20, bold: true, color, fontFace: FONT,
    });
  });
};

const addTrendChartSlide = (pptx, section, primary, accent) => {
  const slide = pptx.addSlide({ masterName: MASTER_NAME });
  addSectionHeader(slide, pptx, section.label, primary, accent);

  let rows = section.rows;
  const truncated = rows.length > MAX_CHART_BUCKETS;
  if (truncated) {
    const step = rows.length / MAX_CHART_BUCKETS;
    rows = Array.from({ length: MAX_CHART_BUCKETS }, (_, i) => section.rows[Math.floor(i * step)]);
  }

  const labels = rows.map((r) => String(r.period));
  const chartData = [
    { name: 'Impressions', labels, values: rows.map((r) => Number(r.impressions) || 0) },
    { name: 'Clicks', labels, values: rows.map((r) => Number(r.clicks) || 0) },
  ];

  slide.addChart(pptx.ChartType.bar, chartData, {
    x: CONTENT_X, y: 1.15, w: CONTENT_W, h: truncated ? 3.6 : 3.85,
    barDir: 'col',
    barGrouping: 'clustered',
    chartColors: [primary, accent],
    showLegend: true,
    legendPos: 'b',
    legendFontSize: 10,
    legendFontFace: FONT,
    catAxisLabelFontSize: 8,
    catAxisLabelFontFace: FONT,
    catAxisLabelRotate: rows.length > 10 ? 45 : 0,
    valAxisLabelFontSize: 8,
    valAxisLabelFontFace: FONT,
    valGridLine: { color: BORDER, style: 'solid', size: 1 },
    catGridLine: { style: 'none' },
  });

  if (truncated) {
    slide.addText(`Showing ${MAX_CHART_BUCKETS} evenly sampled of ${section.rows.length} periods — full data available in CSV export`, {
      x: CONTENT_X, y: 4.85, w: CONTENT_W, h: 0.25,
      fontSize: 8, italic: true, color: SECONDARY, fontFace: FONT,
    });
  }
};

const addTableSlides = (pptx, section, primary, accent) => {
  const isCtrCol = (col) => col.type === 'percent' && /ctr/i.test(col.header);
  const colW = columnWidths(section.columns);

  const chunks = [];
  for (let i = 0; i < section.rows.length && chunks.length < MAX_SLIDES_PER_SECTION; i += ROWS_PER_SLIDE) {
    chunks.push(section.rows.slice(i, i + ROWS_PER_SLIDE));
  }
  const shownRows = chunks.reduce((sum, c) => sum + c.length, 0);
  const truncated = shownRows < section.rows.length;

  chunks.forEach((chunk, chunkIndex) => {
    const slide = pptx.addSlide({ masterName: MASTER_NAME });
    const title = chunks.length > 1
      ? `${section.label} (${chunkIndex + 1} of ${chunks.length})`
      : section.label;
    addSectionHeader(slide, pptx, title, primary, accent);

    const headerRow = section.columns.map((col) => ({
      text: col.header,
      options: { bold: true, color: WHITE, fill: { color: primary }, align: alignOf(col), fontFace: FONT },
    }));

    const bodyRows = chunk.map((row, rowIndex) => section.columns.map((col) => {
      const raw = col.get(row);
      const options = {
        color: DARK,
        fill: { color: rowIndex % 2 ? SUBTLE : WHITE },
        align: alignOf(col),
        fontFace: FONT,
      };
      if (isCtrCol(col)) {
        options.bold = true;
        options.color = (parseFloat(raw) || 0) >= 1 ? accent : DARK;
      }
      return { text: formatValue(raw, col.type), options };
    }));

    slide.addTable([headerRow, ...bodyRows], {
      x: CONTENT_X, y: 1.15, colW,
      rowH: 0.28,
      fontSize: 10,
      fontFace: FONT,
      border: { type: 'solid', pt: 0.5, color: BORDER },
      valign: 'middle',
    });

    if (truncated && chunkIndex === chunks.length - 1) {
      slide.addText(`Showing first ${shownRows} of ${section.rows.length} rows — full data available in CSV export`, {
        x: CONTENT_X, y: 4.95, w: CONTENT_W, h: 0.25,
        fontSize: 8, italic: true, color: SECONDARY, fontFace: FONT,
      });
    }
  });
};

const addClosingSlide = (pptx, meta, accent) => {
  const slide = pptx.addSlide();
  slide.background = { color: DARK };
  slide.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 0.12, h: '100%', fill: { color: accent } });
  slide.addText('Thank You', {
    x: 0, y: 1.9, w: '100%', h: 0.8,
    fontSize: 44, bold: true, color: WHITE, align: 'center', fontFace: FONT,
  });
  slide.addText(`Prepared by ${meta.companyName}`, {
    x: 0, y: 2.85, w: '100%', h: 0.45,
    fontSize: 16, color: '9CA3AF', align: 'center', fontFace: FONT,
  });
  slide.addText(meta.generatedAt, {
    x: 0, y: 3.35, w: '100%', h: 0.35,
    fontSize: 10, color: SECONDARY, align: 'center', fontFace: FONT,
  });
};

export const generatePPTX = async ({ sections, meta }) => {
  const PptxGenJS = await import('pptxgenjs');
  const pptx = new PptxGenJS.default();

  pptx.layout = 'LAYOUT_16x9';
  pptx.author = meta.companyName;
  pptx.company = meta.companyName;
  pptx.title = meta.title;
  pptx.subject = meta.subtitle || meta.pageTypeLabel;

  const primary = hexToPptx(meta.colors.primary);
  const accent = hexToPptx(meta.colors.accent);

  // Content-slide chrome: footer strip + slide number, shared via master
  pptx.defineSlideMaster({
    title: MASTER_NAME,
    background: { color: WHITE },
    objects: [
      { rect: { x: 0, y: 5.25, w: SLIDE_W, h: 0.375, fill: { color: SUBTLE } } },
      { text: { text: meta.companyName, options: { x: 0.3, y: 5.28, w: 3.5, h: 0.3, fontSize: 8, bold: true, color: primary, fontFace: FONT } } },
      { text: { text: meta.dateRangeLabel, options: { x: 6.2, y: 5.28, w: 3.5, h: 0.3, fontSize: 8, color: SECONDARY, align: 'right', fontFace: FONT } } },
    ],
    slideNumber: { x: 4.6, y: 5.28, w: 0.8, h: 0.3, fontSize: 8, color: SECONDARY, align: 'center', fontFace: FONT },
  });

  addTitleSlide(pptx, meta, primary, accent);

  sections.forEach((section) => {
    if (section.type === 'metrics') {
      addSummarySlide(pptx, section, primary, accent);
      return;
    }
    if (section.key === 'trend') {
      addTrendChartSlide(pptx, section, primary, accent);
    }
    addTableSlides(pptx, section, primary, accent);
  });

  addClosingSlide(pptx, meta, accent);

  const date = new Date().toISOString().split('T')[0];
  await pptx.writeFile({ fileName: `${slug(meta.companyName)}_${slug(meta.pageTypeLabel)}_${date}.pptx` });
};
